import { useAuth0 } from '@auth0/auth0-react'
import { consumeForcedLogoutReason } from '@/lib/recoveryRefreshLoop'
import { useCallback, useEffect, useMemo, useRef } from 'react'

export function LogoutCompletePage(): JSX.Element {
  const { loginWithRedirect } = useAuth0()
  const forcedReason = useMemo(() => consumeForcedLogoutReason(), [])
  const redirectInFlightRef = useRef(false)

  // Don't auto-redirect when we were force signed out to recover from a
  // refresh loop — bouncing straight back to login risks re-entering it, and
  // the user should see the explanation before signing in again.
  const shouldAutoRedirect = forcedReason !== 'recovery-refresh-loop'

  const handleLogin = useCallback(async (): Promise<void> => {
    if (redirectInFlightRef.current) {
      return
    }
    redirectInFlightRef.current = true
    try {
      await loginWithRedirect({
        appState: { returnTo: '/' },
      })
    } catch (error) {
      console.warn('Auth redirect failed', error)
      redirectInFlightRef.current = false
    }
  }, [loginWithRedirect])

  useEffect(() => {
    if (!shouldAutoRedirect) {
      return
    }
    void handleLogin()
  }, [shouldAutoRedirect, handleLogin])

  if (shouldAutoRedirect) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy text-white">
        <div className="text-center">
          <p className="text-lg font-semibold">Redirecting to sign in…</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy px-4 text-white">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-navy-light px-6 py-8 text-center">
        <h1 className="text-2xl font-semibold">Signed out</h1>
        <p className="mt-2 text-sm text-white/75">
          You have signed out of the Medical Congress Solution Platform. Sign back in to resume syncing and
          capturing new insights online.
        </p>
        <p className="mt-3 text-sm text-white/75">
          We signed you out to recover from a repeated refresh loop after the app was suspended.
          Your locally saved drafts and recordings are still on this device.
        </p>
        <button
          type="button"
          className="mt-6 w-full rounded-xl bg-cyan-500 px-4 py-3 text-sm font-semibold text-navy transition hover:bg-cyan-400"
          onClick={handleLogin}
        >
          Sign in again
        </button>
      </div>
    </div>
  )
}
